import React, { useState, useEffect, Fragment, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'components/Shared/Toast'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Button, Input, Form, Label, FormGroup, FormFeedback, TabContent, TabPane, Nav, NavItem, NavLink,
    InputGroup, InputGroupAddon, ModalBody, ModalHeader, Modal
} from 'reactstrap';
import { fetcher } from 'api/client';
import styled from 'styled-components';

import { createSuggestion, fetchSearch, fetchSearchSuggestions, deleteSearchSuggestion } from 'api/actions/search';
import PdfPreview from 'components/Shared/PdfPreview'
import { truncate } from 'utils';
import useDebounce from 'hooks/useDebounce';

const SuggestionSchema = Yup.object().shape({
    name: Yup.string()
        .required('Required'),
    text: Yup.string()
        .required('Required')
});

const RightSide = styled.div`
    width: ${props => `calc(100% - ${props.width}px)`} 
`

const PDF_WIDTH = 595;

function AnalysisViewerModal({ isOpen, search, selectedDocument, onClose }) {
    const [highlightSearch, setHighlightSearch] = useState('')
    const { t } = useTranslation()
    const queryClient = useQueryClient();
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('1');
    const { register, handleSubmit, errors, setValue, reset } = useForm({
        resolver: yupResolver(SuggestionSchema)
    });
    const searchSuggestionsId = ['search', search._id, 'document', selectedDocument._id, 'suggestions'];
    const [searchText, setSearchText] = useState('');
    const debouncedSearch = useDebounce(searchText, 500);

    const { mutate: mutateCreateSuggestion } = useMutation((payload) => fetcher(createSuggestion(payload)), {
        onSuccess: (data) => {
            toast.success(t('metadata.success_save'))
            // t('validations:successfullySaved', { context: 'male', entitity: 'análisis'})
            queryClient.setQueryData(searchSuggestionsId, old => ([
                ...old,
                data.data
            ]))
            reset()
        }
    })


    const keywordList = useMemo(() => {
        if (!debouncedSearch || debouncedSearch.length < 3) {
            return selectedDocument?.wordsAnalized ?? []
        }

        return selectedDocument?.wordsAnalized?.filter?.(keyword => keyword.word.match(new RegExp(debouncedSearch, 'ig'))) ?? []
    }, [selectedDocument.wordsAnalized, debouncedSearch])

    const handleSelectText = () => {
        setValue('text', window.getSelection().toString())
    };

    const handleSearch = (evt) => {
        setSearchText(evt.target.value)
    };

    const handleSaveSuggestion = async (formData) => {
        mutateCreateSuggestion({
            contractId: selectedDocument.contract._id,
            documentId: selectedDocument._id,
            searchId: search._id,
            text: formData.text,
            name: formData.name
        })
    };

    const handleHightlightSearchChange = (keyword) => {
        setHighlightSearch(keyword.word)
    }

    const goTo = useCallback((page) => {
        setCurrentPage(page)
    }, [])

    const handleClose = () => {
        reset()
        setSearchText('')
        onClose()
    }

    const toggleTabs = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    }


    if (!isOpen) {
        return null
    }

    return (
        <Modal isOpen={isOpen} centered className={'modal-lg'}>
            <ModalHeader toggle={handleClose}>
                {/* {t('contracts.reference.title')} */}
                {selectedDocument?.contract?.name ?? 'PDF'}
                {/* <span className="sub-title">{t('contracts.reference.subtitle')}</span> */}

            </ModalHeader>
            <ModalBody className="d-flex" style={{ overflow: 'scroll' }}>
                <div>
                    <PdfPreview
                        url={selectedDocument.url}
                        pageNumber={currentPage}
                        width={PDF_WIDTH}
                        highlightSearch={highlightSearch}
                        onMouseUp={handleSelectText}
                        onPageChange={goTo}
                        controls
                    />
                </div>
                <RightSide className="px-5" width={PDF_WIDTH}>
                    <div className="mb-3">
                        {/* <input 
                                    placeholder={t('metadata.search')}
                                    className={`form-control search-input align-self-center border rounded`}
                                    value={searchText}
                                    onChange={handleSearch}
                                />  */}
                        <InputGroup className="rounded-lg">
                            <Input
                                className="border-right-0 border rounded-left"
                                placeholder={t('metadata.search')}
                                value={searchText}
                                onChange={handleSearch}
                            />
                            <InputGroupAddon addonType="append" >
                                <Button size="md" color="white" className="p-2 form-control border border-left-0 rounded-right ">
                                    <i className="fas fa-search"></i>
                                </Button>
                            </InputGroupAddon>
                        </InputGroup>
                        <h4 className="font-bold mt-3">{t('metadata.found_words')}</h4>
                        <KeywordList
                            list={keywordList}
                            onKeywordClick={handleHightlightSearchChange}
                            onGoToPage={goTo}
                            currentPage={currentPage}
                        />
                    </div>
                    <div className="">
                        {/* <div className="tabs-header">
                                    <span className={clsx('tabs-header-item', activeTab === '1' && 'active')} onClick={() => { toggleTabs('1'); }}>Editar</span>
                                    <span className={clsx('tabs-header-item', activeTab === '2' && 'active')} onClick={() => { toggleTabs('2'); }}>Mis cambios</span>
                                </div> */}
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    tag="a"
                                    className={clsx('text-dark font-weight-thin pointer', activeTab === '1' && 'active', activeTab !== '1' && 'bg-grey border')}
                                    onClick={() => { toggleTabs('1'); }}
                                >
                                    {t('metadata.edit')}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    tag="a"
                                    className={clsx('text-dark font-weight-thin pointer', activeTab === '2' && 'active', activeTab !== '2' && 'bg-grey border')}
                                    onClick={() => { toggleTabs('2'); }}
                                >
                                    {t('metadata.analysis')}
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <TabContent className="py-3" activeTab={activeTab}>
                            <TabPane tabId="1">
                                <Form onSubmit={handleSubmit(handleSaveSuggestion)} noValidate>
                                    <FormGroup>
                                        <Label>{t('metadata.name')}</Label>
                                        <Input
                                            type="text"
                                            name="name"
                                            innerRef={register}
                                            invalid={!!errors.name}
                                        />
                                        <FormFeedback>{errors?.name?.message}</FormFeedback>
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>{t('metadata.content')}</Label>
                                        <Input
                                            className="mxw-100 mxh-60vh"
                                            name="text"
                                            type="textarea"
                                            placeholder={t('metadata.select_text')}
                                            style={{
                                                minWidth: 500,
                                                minHeight: 250,
                                            }}
                                            invalid={!!errors.text}
                                            innerRef={register}
                                        />
                                        <FormFeedback>{errors?.text?.message}</FormFeedback>
                                    </FormGroup>
                                    <Button type="submit">{t('metadata.save')}</Button>
                                </Form>
                            </TabPane>
                            <TabPane tabId="2">
                                <SuggestionList
                                    searchId={search._id}
                                    documentId={selectedDocument._id}
                                />
                            </TabPane>
                        </TabContent>
                    </div>
                </RightSide>
            </ModalBody>
        </Modal>
    )
}

export function SuggestionList({ searchId, documentId, showOrigin, contentHeight }) {
    const { t } = useTranslation();
    const [copiedSuggestionId, setCopiedSuggestionId] = useState('');
    const queryClient = useQueryClient();
    const searchSuggestionsId = useMemo(() => {
        let id = ['search', searchId]
        if (documentId) {
            id.push('document')
            id.push(documentId)
        }
        id.push('suggestions')
        return id;
    }, [searchId, documentId]);

    const { status, data, error } = useQuery(searchSuggestionsId,
        () => fetcher(fetchSearchSuggestions({ searchId, documentId })),
        {
            enabled: !!searchId
        }
    )
    const { mutate: mutateDeleteSuggestion } = useMutation((payload) => fetcher(deleteSearchSuggestion(payload)))

    const handleCopyToClipboard = (suggestion) => {
        navigator.clipboard.writeText(suggestion.text)
        toast.success(t('common.copied_clipboard'));
        setCopiedSuggestionId(suggestion._id)
    }

    const handleDeleteSuggestion = async (suggestion) => {
        const data = await mutateDeleteSuggestion({ searchId: searchId, suggestionId: suggestion._id })

        if (data.success) {
            queryClient.setQueryData(searchSuggestionsId, old => old.filter(sugg => sugg._id !== suggestion._id))
        }
    }

    return (
        <div>
            {
                status === 'loading' &&
                <span>{t('metadata.uploading')}...</span>
            }
            {
                data?.map(suggestion => (
                    <SuggestionItem
                        showOrigin={showOrigin}
                        suggestion={suggestion}
                        isCopied={copiedSuggestionId === suggestion._id}
                        onCopyToClipboard={handleCopyToClipboard}
                        onDelete={handleDeleteSuggestion}
                        contentHeight={contentHeight}
                    />
                ))
            }
            {
                status === 'success' && !data?.length && (
                    <span>{t('metadata.not_saved')}</span>
                )
            }
        </div>
    )
}

function SuggestionItem({ suggestion, isCopied, onCopyToClipboard, onDelete, showOrigin, contentHeight }) {
    const { t } = useTranslation();
    const handleCopyToClipboard = () => {
        onCopyToClipboard(suggestion)
    }

    const handleDelete = () => {
        onDelete(suggestion)
    }

    return (
        <div className="p-3 border rounded mb-2">
            <div className="font-bold">
                {suggestion.name}
            </div>

            {showOrigin && <small className="d-block text-muted mb-2">
                {t('metadata.origin')}: {suggestion?.contract?.name}
            </small>}
            <div className="overflow-auto" style={{ maxHeight: contentHeight }}>
                {suggestion.text}
            </div>
            <div className="text-muted d-flex justify-content-end">
                <span className="px-1 mr-2 hover_text-decoration-underline" onClick={handleCopyToClipboard}>
                    <i className="fas fa-copy mr-1"></i> {isCopied ? t('metadata.copied') : t('metadata.copy')}
                </span>
                <span className="px-1 hover_text-decoration-underline" onClick={handleDelete}>
                    <i className="fas fa-trash mr-1"></i> {t('metadata.delete')}
                </span>
            </div>
        </div>
    )
}

function KeywordList({ list, onGoToPage, onKeywordClick, currentPage }) {
    const { t } = useTranslation();
    const [currentKeyword, setCurrentKeyword] = useState({});
    const keywordList = useMemo(() => {
        return list.filter(keyword => keyword.locations).map(keyword => {
            const pages = Array.from(new Set(keyword?.locations?.map?.(l => l.page)))
            const totalFound = keyword?.locations?.length;

            return { ...keyword, pages, totalFound }
        })
    }, [list])

    const handleKeywordClick = (keyword) => {
        setCurrentKeyword(keyword)
        onGoToPage(keyword.pages[0])
        onKeywordClick(keyword)
    }

    const handleCloseKeywordDetail = () => {
        setCurrentKeyword({})
        onKeywordClick({ word: '' })
    }


    if (!list.length) {
        return <span>{t('metadata.not_found')}</span>
    }

    return (
        <div className="mb-2">
            <div className="d-flex mb-2 flex-wrap">
                {
                    keywordList.map(keyword => {
                        return (
                            <KeywordItem
                                className='mr-2 mb-2'
                                keyword={keyword}
                                isActive={keyword.word === currentKeyword.word}
                                onGoToPage={onGoToPage}
                                onClick={handleKeywordClick}
                            />
                        )
                    })
                }
            </div>
            { currentKeyword.word &&
                <KeywordDetail
                    keyword={currentKeyword}
                    currentPage={currentPage}
                    onGoToPage={onGoToPage}
                    onClose={handleCloseKeywordDetail}
                />
            }



        </div>
    )
}


function KeywordItem({ className, keyword, onClick, isActive }) {
    const handleClick = () => onClick(keyword)

    return (
        <div
            className={clsx('border text-center py-2 px-3', className, isActive && 'bg-primary text-light', !isActive && 'text-muted')}
            style={{ borderRadius: 40 }}
            onClick={handleClick}
        >
            {keyword.word} ({keyword.totalFound})
        </div>
    )
}

function KeywordDetail({ keyword, onGoToPage, onClose, currentPage }) {
    const handleGoToPage = (page) => () => onGoToPage(page)
    const { t } = useTranslation();

    return (
        <div className="p-1 bg-light shadow rounded border" style={{ maxWidth: 200 }}>
            <div className="d-flex justify-content-between border-bottom font-bold">
                <span>{keyword.word} ({keyword.totalFound})</span>
                <span onClick={onClose}>X</span>
            </div>
            <div>
                <div>
                    {t('metadata.found_pages')}
                </div>
                <div className="p-2 d-flex overflow-auto py-3">
                    {keyword.pages.map(p =>
                        <div
                            className={clsx('d-flex rounded-circle border align-items-center justify-content-center mr-2', currentPage === p && 'bg-primary')}
                            style={{ minWidth: '30px', minHeight: '30px' }}
                            onClick={handleGoToPage(p)}
                        >
                            <span>{p}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AnalysisViewerModal;
